import * as fs from 'fs';
import * as path from 'path';
import { buildSurveyUrl } from '../fetchSurvey/utils/url';
import { mapSurvey } from '../fetchSurvey/utils/mappers/survey/mapSurvey';
import type { ApiResponse } from '../fetchSurvey/internalTypes';
import type { QuestionTypeLiteral } from './ui-config-types';
import { QUESTION_TYPE_LITERALS } from './ui-config-types';

function parseEnvFile(content: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of content.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const eq = trimmed.indexOf('=');
    if (eq <= 0) continue;
    const key = trimmed.slice(0, eq).trim();
    let value = trimmed.slice(eq + 1).trim();
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    out[key] = value;
  }
  return out;
}

function loadEnvVars(projectRoot: string): Record<string, string> {
  const merged: Record<string, string> = { ...process.env } as Record<string, string>;
  for (const file of ['.env', '.env.local']) {
    const abs = path.join(projectRoot, file);
    if (!fs.existsSync(abs)) continue;
    Object.assign(merged, parseEnvFile(fs.readFileSync(abs, 'utf8')));
  }
  return merged;
}

export function resolveInstanceIdFromEnv(
  projectRoot: string,
  override?: string,
): string | null {
  if (override?.trim()) return override.trim();
  const env = loadEnvVars(projectRoot);
  const id =
    env.VITE_CFM_INSTANCE_ID?.trim() ||
    env.NEXT_PUBLIC_CFM_INSTANCE_ID?.trim() ||
    env.CFM_INSTANCE_ID?.trim();
  return id || null;
}

export function extractSurveyDisplayName(api: ApiResponse): string | null {
  const name = api.cfmSurveyDTO?.name?.trim();
  return name || null;
}

export function extractSurveyTypesFromSurvey(
  survey: ReturnType<typeof mapSurvey>,
): QuestionTypeLiteral[] {
  const types = new Set<QuestionTypeLiteral>();
  for (const page of survey.pages) {
    for (const question of page.questions) {
      const t = question.type;
      if ((QUESTION_TYPE_LITERALS as readonly string[]).includes(t)) {
        types.add(t as QuestionTypeLiteral);
      }
    }
  }
  return [...types].sort();
}

export interface FetchSurveyInventoryResult {
  instanceId: string;
  surveyTypes: QuestionTypeLiteral[];
}

export async function fetchSurveyTypesFromEnv(
  projectRoot: string,
  instanceIdOverride?: string,
): Promise<FetchSurveyInventoryResult> {
  const { instanceId, survey } = await fetchSurveyFromEnv(projectRoot, instanceIdOverride);
  const surveyTypes = extractSurveyTypesFromSurvey(survey);

  if (!surveyTypes.length) {
    throw new Error('Survey fetch returned no question types — check instance ID');
  }

  return { instanceId, surveyTypes };
}

export interface FetchSurveyResult {
  instanceId: string;
  survey: ReturnType<typeof mapSurvey>;
  apiResponse: ApiResponse;
}

export async function fetchSurveyFromEnv(
  projectRoot: string,
  instanceIdOverride?: string,
): Promise<FetchSurveyResult> {
  const instanceId = resolveInstanceIdFromEnv(projectRoot, instanceIdOverride);
  if (!instanceId) {
    throw new Error(
      'no instance ID in env — paste JWT into .env (VITE_CFM_INSTANCE_ID) or .env.local (NEXT_PUBLIC_CFM_INSTANCE_ID). Fix: instance-id-quick-wire.md',
    );
  }

  const url = buildSurveyUrl(instanceId);
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Survey fetch network error: ${msg}`);
  }

  if (!response.ok) {
    throw new Error(
      `Survey fetch failed (${response.status}) — check JWT is fresh. Fix: instance-id-quick-wire.md`,
    );
  }

  const apiResponse = (await response.json()) as ApiResponse;
  const survey = mapSurvey(apiResponse);
  return { instanceId, survey, apiResponse };
}
